import { FileText, Phone, PhoneIncoming, PhoneMissed, PhoneOutgoing, Play } from 'lucide-react';
import type { CallItem } from '@/api/calls';
import { callName, clock, isMissed, isMissedOpen, isUnreached, outcomeWords, timeOf } from '@/lib/callsQuery';
import { FALLBACK_STAGE_COLOR } from '@/lib/dealCard';

interface Props {
  c: CallItem;
  /** Показывать сотрудника (звонки команды). */
  showAgent: boolean;
  onOpen: (c: CallItem) => void;
  onCallBack: (c: CallItem) => void;
}

/** Класс значка направления: исходящий — голубой неон, входящий — салатовый, пропущенный — красный. */
export function dirClass(c: Pick<CallItem, 'direction' | 'status'>): string {
  if (isMissed(c)) return 'miss';
  if (isUnreached(c)) return 'noans';
  return c.direction === 'incoming' ? 'in' : 'out';
}

export function DirIcon({ c }: { c: Pick<CallItem, 'direction' | 'status'> }) {
  if (isMissed(c)) return <PhoneMissed aria-hidden />;
  return c.direction === 'incoming' ? <PhoneIncoming aria-hidden /> : <PhoneOutgoing aria-hidden />;
}

/** Строка журнала (прототип: crow). Пропущенный без перезвона — кнопка «Перезвонить» прямо в строке. */
export function CallRow({ c, showAgent, onOpen, onCallBack }: Props) {
  const name = callName(c);
  const open = isMissedOpen(c);
  let sub: React.ReactNode;
  if (isMissed(c)) {
    sub = open ? <span className="red">Пропущенный, не перезвонили</span> : <span>Пропущенный, перезвонили{c.callback?.byName ? ` (${c.callback.byName})` : ''}</span>;
  } else if (isUnreached(c)) {
    sub = <span>{outcomeWords(c)}</span>;
  } else {
    sub = (
      <span>
        {c.stageName && <span className="sdot" style={{ background: c.stageColor || FALLBACK_STAGE_COLOR }} aria-hidden />}
        {[c.companyName && c.companyName !== name ? c.companyName : null, c.stageName ?? c.pipelineName, c.outcome === 'live' ? outcomeWords(c) : null]
          .filter(Boolean).join(' · ') || c.phone}
      </span>
    );
  }
  const hasRec = c.recordingState === 'stored';
  const hasTr = c.transcriptState === 'done';
  const label = `${c.direction === 'incoming' ? 'Входящий' : 'Исходящий'} ${timeOf(c.occurredAt)}, ${name}, ${outcomeWords(c)}`;
  return (
    <div className="crow" role="button" tabIndex={0} aria-label={label}
      onClick={() => onOpen(c)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(c); } }}>
      <span className={`dir ${dirClass(c)}`}><DirIcon c={c} /></span>
      <span className="cmain"><b>{name}</b>{sub}</span>
      {open ? (
        <button type="button" className="cbk" aria-label={`Перезвонить: ${name}`} onClick={(e) => { e.stopPropagation(); onCallBack(c); }}>
          <Phone aria-hidden />
        </button>
      ) : (
        <span className="cside tnum">
          <span>{timeOf(c.occurredAt)}</span>
          {!!c.durationSeconds && c.outcome === 'answered' && <span className="du">{clock(c.durationSeconds)}</span>}
          {(hasRec || hasTr) && (
            <span className="marks">
              {hasRec && <Play aria-label="Есть запись" />}
              {hasTr && <FileText className={c.transcriptDraft ? 'dr' : undefined} aria-label={c.transcriptDraft ? 'Черновая расшифровка' : 'Есть расшифровка'} />}
            </span>
          )}
          {showAgent && c.agentName && <span>{c.agentName}</span>}
        </span>
      )}
    </div>
  );
}
